/* global PowerPoint */

import { INSPECTION_STATUS } from '../FinanceOfficeOperationRegistry';
import {
  assertCurrentDocumentIdentity,
  assertNotAborted,
  containsLicensedDataEvidence,
  targetDrift,
  verificationFailed,
  writerError,
} from './common';

const SHAPE_PREFIX = 'RestackFinance:';

function operationShapePrefix(operationId) {
  return `${SHAPE_PREFIX}${operationId}:`;
}

function expectedShapes(request) {
  const shapes = request.payload.blocks.map((block, index) => ({
    name: `${operationShapePrefix(request.operationId)}block:${index + 1}`,
    text: block.text,
    role: block.role,
  }));
  shapes.push({
    name: `${operationShapePrefix(request.operationId)}sources`,
    text: request.payload.citationLines.join('\r'),
    role: 'source',
  });
  return shapes;
}

function geometryFor(index, count, role, slideWidth, slideHeight) {
  if (role === 'title') {
    return {
      left: 36,
      top: 24,
      width: Math.max(120, slideWidth - 72),
      height: 54,
    };
  }
  if (role === 'source') {
    return {
      left: 36,
      top: Math.max(0, slideHeight - 70),
      width: Math.max(120, slideWidth - 72),
      height: 46,
    };
  }
  const bodyTop = 92;
  const bodyBottom = Math.max(bodyTop + 40, slideHeight - 86);
  const rowHeight = Math.max(24, Math.floor((bodyBottom - bodyTop) / Math.max(1, count)));
  return {
    left: 48,
    top: bodyTop + (index * rowHeight),
    width: Math.max(120, slideWidth - 96),
    height: rowHeight,
  };
}

function shapeFont(role) {
  if (role === 'title') return { size: 24, bold: true, color: '#222222' };
  if (role === 'metric') return { size: 16, bold: true, color: '#222222' };
  if (role === 'source') return { size: 7, bold: false, color: '#595959' };
  return { size: 12, bold: false, color: '#222222' };
}

export class PowerPointFinanceWriter {
  constructor({
    run = (callback) => {
      if (typeof PowerPoint === 'undefined' || typeof PowerPoint.run !== 'function') {
        throw writerError(
          'finance_office_unavailable',
          'PowerPoint API is unavailable'
        );
      }
      return PowerPoint.run(callback);
    },
  } = {}) {
    this.run = run;
    this.supportsReadOnlyLiveDataTarget = true;
  }

  async detectLicensedDataEvidence() {
    return this.run(async (context) => {
      const slides = context.presentation.slides;
      slides.load('items/id');
      await context.sync();
      for (const slide of slides.items) {
        slide.shapes.load('items/name');
      }
      await context.sync();
      for (const slide of slides.items) {
        for (const shape of slide.shapes.items) {
          if (String(shape.name || '').startsWith(SHAPE_PREFIX)) return true;
          try {
            const textRange = shape.textFrame.textRange;
            textRange.load('text');
            await context.sync();
            if (containsLicensedDataEvidence(textRange.text)) return true;
          } catch {
            // Shapes without text frames cannot contain a citation sentinel.
          }
        }
      }
      return false;
    });
  }

  async prepareLiveDataTarget({
    signal,
    assertCurrentIdentity,
  } = {}) {
    assertNotAborted(signal);
    return this.run(async (context) => {
      const selectedSlides = context.presentation.getSelectedSlides();
      selectedSlides.load('items/id');
      await context.sync();
      if (selectedSlides.items.length !== 1) {
        throw targetDrift('Select exactly one empty PowerPoint slide');
      }
      const slide = selectedSlides.items[0];
      const shapes = slide.shapes;
      shapes.load('items/name');
      await context.sync();
      assertNotAborted(signal);
      await assertCurrentDocumentIdentity(assertCurrentIdentity);
      if (shapes.items.length !== 0) {
        throw targetDrift('The selected PowerPoint slide is not empty');
      }
      return Object.freeze({
        app: 'powerpoint',
        slideId: slide.id,
        requireEmpty: true,
      });
    });
  }

  async _loadTarget(context, request) {
    const slides = context.presentation.slides;
    slides.load('items/id');
    await context.sync();
    const matches = slides.items.filter((slide) => slide.id === request.target.slideId);
    if (matches.length !== 1) {
      throw targetDrift('The PowerPoint target slide no longer exists');
    }
    const slide = matches[0];
    const shapes = slide.shapes;
    shapes.load('items/name');
    await context.sync();
    return { slide, shapes };
  }

  async prepare(request, {
    signal,
    assertCurrentIdentity,
  } = {}) {
    assertNotAborted(signal);
    return this.run(async (context) => {
      const { shapes } = await this._loadTarget(context, request);
      assertNotAborted(signal);
      await assertCurrentDocumentIdentity(assertCurrentIdentity);
      if (shapes.items.length !== 0) {
        throw targetDrift('The PowerPoint target slide is not empty');
      }
      return {
        targetType: 'slide',
        stableTargetId: request.target.slideId,
      };
    });
  }

  async inspect(request, {
    signal,
    assertCurrentIdentity,
  } = {}) {
    assertNotAborted(signal);
    return this.run(async (context) => {
      const { shapes } = await this._loadTarget(context, request);
      await assertCurrentDocumentIdentity(assertCurrentIdentity);
      const expected = expectedShapes(request);
      const ownPrefix = operationShapePrefix(request.operationId);
      const ownShapes = shapes.items.filter((shape) =>
        String(shape.name || '').startsWith(ownPrefix)
      );
      const otherShapes = shapes.items.filter((shape) =>
        !String(shape.name || '').startsWith(ownPrefix)
      );
      if (ownShapes.length === 0 && otherShapes.length === 0) {
        return { status: INSPECTION_STATUS.NOT_COMMITTED, mutationCount: 0 };
      }
      if (otherShapes.length > 0 || ownShapes.length !== expected.length) {
        return {
          status: INSPECTION_STATUS.INDETERMINATE,
          mutationCount: ownShapes.length,
        };
      }
      for (const shape of ownShapes) {
        shape.textFrame.textRange.load('text');
      }
      await context.sync();
      await assertCurrentDocumentIdentity(assertCurrentIdentity);
      const committed = expected.every((item) => {
        const shape = ownShapes.find((candidate) => candidate.name === item.name);
        return shape && String(shape.textFrame.textRange.text || '') === item.text;
      });
      return {
        status: committed
          ? INSPECTION_STATUS.COMMITTED
          : INSPECTION_STATUS.INDETERMINATE,
        mutationCount: ownShapes.length,
      };
    });
  }

  async commit(request, {
    signal,
    assertCurrentIdentity,
  } = {}) {
    assertNotAborted(signal);
    return this.run(async (context) => {
      const { slide, shapes } = await this._loadTarget(context, request);
      assertNotAborted(signal);
      await assertCurrentDocumentIdentity(assertCurrentIdentity);
      if (shapes.items.length !== 0) {
        throw targetDrift('The PowerPoint target slide changed before commit');
      }
      let slideWidth = 960;
      let slideHeight = 540;
      try {
        context.presentation.load('slideWidth,slideHeight');
        await context.sync();
        slideWidth = Number(context.presentation.slideWidth) || slideWidth;
        slideHeight = Number(context.presentation.slideHeight) || slideHeight;
      } catch {
        // Use standard presentation dimensions on older hosts.
      }
      assertNotAborted(signal);
      await assertCurrentDocumentIdentity(assertCurrentIdentity);
      const expected = expectedShapes(request);
      const bodyCount = expected.filter((item) =>
        item.role !== 'title' && item.role !== 'source'
      ).length;
      let bodyIndex = 0;
      const inserted = expected.map((item) => {
        const index = item.role === 'title' || item.role === 'source'
          ? 0
          : bodyIndex++;
        const shape = slide.shapes.addTextBox(
          item.text,
          geometryFor(index, bodyCount, item.role, slideWidth, slideHeight)
        );
        shape.name = item.name;
        const font = shape.textFrame.textRange.font;
        const style = shapeFont(item.role);
        font.size = style.size;
        font.bold = style.bold;
        font.color = style.color;
        shape.load('name');
        shape.textFrame.textRange.load('text');
        return shape;
      });
      await context.sync();
      const verified = expected.every((item, index) =>
        inserted[index].name === item.name &&
        String(inserted[index].textFrame.textRange.text || '') === item.text
      );
      if (!verified) throw verificationFailed();
      return { committed: true, mutationCount: inserted.length };
    });
  }
}

export default PowerPointFinanceWriter;
